// @ts-nocheck

import React, { useMemo, useState, useRef } from 'react';

import uploadIcon from '@assets/img/icons/upload.svg';
import { FormProps } from '@components/admin/FormDialog';
import { SingleUploadButton, TextInput } from '@components/admin/Forms';
import { SpinnerModal } from '@components/SpinnerModal';
import { cargoTrackingStrings, trackingDataMeta } from '@data/cargoTracking';
import { usePatchTrackingData } from '@hooks/mutation/dashboard/usePatchTrackingData';
import {
  CargoCoop,
  cargoCoops,
  DomesticTrackingDetailItem,
  isCargoCompany,
  TrackingDetailItem,
  TransportStatus,
} from '@models/TrackingData';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, remoteFileField, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatInputDate, formatInputTime, formatISOString, id } from '@utils/types';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';

const strings = createStrings({
  ja: {
    uploadFiles: (
      <>
        書類アップロード
        <img src={uploadIcon} alt="Upload" height={16} width={16} />
      </>
    ),
    fileRequired: '書類は必須です',
  },
});

const useStyles = makeMuiStyles(() => ({}));

export interface TrackingDetailsFormProps extends FormProps {
  trackNo: string;
  transportStatus: TransportStatus;
  item?: TrackingDetailItem | null;
  noFile?: boolean;
}

export const TrackingDetailsForm: React.FC<TrackingDetailsFormProps> = (props) => {
  const { trackNo, transportStatus, item, formikRef, noFile = false } = props;

  const schema = useMemo(() => {
    const baseSchemaFields = {
      place: requiredStringField(cargoTrackingStrings.place),
      date: requiredStringField(cargoTrackingStrings.date),
      time: requiredStringField(cargoTrackingStrings.time),
    };

    if (noFile) {
      return t.type({
        ...baseSchemaFields,
      });
    }
    return t.type({
      ...baseSchemaFields,
      file: remoteFileField(cargoTrackingStrings.files),
    });
  }, [noFile]);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { mutateAsync, isLoading } = usePatchTrackingData(trackNo);

  const timeZoneInfo = trackingDataMeta[transportStatus];

  const initialValues = useMemo(() => {
    const date = new Date(item?.time ?? new Date());
    return {
      trackNo,
      place: item?.place ?? '',
      date: formatInputDate(date, timeZoneInfo.id),
      time: formatInputTime(date, timeZoneInfo.id),
      file: noFile
        ? undefined
        : {
            file: undefined,
            remote: item?.file,
          },
    };
  }, []);

  return (
    <Formik<t.TypeOf<typeof schema>>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={createValidate(schema)}
      onSubmit={async (values) => {
        const { place, date, time } = values;

        const isoTime = formatISOString(date, time, timeZoneInfo.id);

        await mutateAsync({
          transport_status: transportStatus,
          place,
          time: isoTime,
          coop_name: undefined,
          coop_tracking_no: undefined,
          car_no: undefined,
          car_driver: undefined,
          file: 'file' in values ? values?.file?.file : undefined,
        });
      }}
    >
      <Form className={classes.verticalSpace1}>
        <SpinnerModal open={isLoading} />

        <TextInput label={cargoTrackingStrings.d2dNo} name="trackNo" disabled />
        <TextInput label={cargoTrackingStrings.place} name="place" />
        <TextInput label={cargoTrackingStrings.date} name="date" type="date" />
        <TextInput label={cargoTrackingStrings.time} name="time" type="time" />
        {!noFile && (
          <SingleUploadButton
            label={cargoTrackingStrings.files}
            name="file"
            text={strings.uploadFiles}
          />
        )}
      </Form>
    </Formik>
  );
};

export interface DomesticTrackingDetailsFormProps extends FormProps {
  trackNo: string;
  transportStatus: TransportStatus;
  item?: DomesticTrackingDetailItem | null;
}

export const DomesticTrackingDetailsForm: React.FC<DomesticTrackingDetailsFormProps> = (props) => {
  const { trackNo, transportStatus, item, formikRef } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const coopNameRef = useRef(item?.coop_name ?? '');
  const [coopName, setCoopName] = useState(coopNameRef.current);

  const { mutateAsync, isLoading } = usePatchTrackingData(trackNo);

  const meta = trackingDataMeta[transportStatus];

  const initialValues = useMemo(() => {
    const date = new Date(item?.time ?? new Date());

    return {
      d2dNo: trackNo,
      date: formatInputDate(date, meta.id),
      time: formatInputTime(date, meta.id),
      coopName: item?.coop_name ?? '',
      coopTrackingNo: item?.coop_tracking_no ?? '',
      carNo: item?.car_no ?? '',
      carDriver: item?.car_driver ?? '',
    };
  }, []);

  const schema = (_coopName: string) =>
    t.type({
      date: requiredStringField(cargoTrackingStrings.date),
      time: requiredStringField(cargoTrackingStrings.time),
      coopName: requiredStringField(cargoTrackingStrings.domesticMethod),
      coopTrackingNo: t.string,
      carNo: t.string,
      carDriver: t.string,
      ...(isCargoCompany(_coopName) && {
        coopTrackingNo: requiredStringField(cargoTrackingStrings.coopTrackNo),
      }),
      ...(_coopName === id<CargoCoop>('チャータートラック') && {
        carNo: requiredStringField(cargoTrackingStrings.carNo),
        carDriver: requiredStringField(cargoTrackingStrings.carDriver),
      }),
    });

  return (
    <Formik<t.TypeOf<ReturnType<typeof schema>>>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={(value) => {
        coopNameRef.current = value.coopName;
        setCoopName(value.coopName);
        return createValidate(schema(coopNameRef.current))(value);
      }}
      onSubmit={async (values) => {
        const {
          // eslint-disable-next-line @typescript-eslint/no-shadow
          coopName,
          coopTrackingNo,
          carNo,
          carDriver,
          date,
          time,
        } = values;

        const isoTime = formatISOString(date, time, meta.id);

        if (isCargoCompany(coopName)) {
          await mutateAsync({
            transport_status: transportStatus,
            place: undefined,
            time: isoTime,
            coop_name: coopName,
            coop_tracking_no: coopTrackingNo,
            car_no: undefined,
            car_driver: undefined,
            file: undefined,
          });
        } else {
          await mutateAsync({
            transport_status: transportStatus,
            place: undefined,
            time: isoTime,
            coop_name: coopName,
            coop_tracking_no: coopTrackingNo,
            car_no: carNo,
            car_driver: carDriver,
            file: undefined,
          });
        }
      }}
    >
      <Form className={classes.verticalSpace1}>
        <SpinnerModal open={isLoading} />

        <TextInput label={cargoTrackingStrings.d2dNo} name="d2dNo" disabled />
        <TextInput label={cargoTrackingStrings.date} name="date" type="date" />
        <TextInput label={cargoTrackingStrings.time} name="time" type="time" />
        <TextInput
          label={cargoTrackingStrings.domesticMethod}
          name="coopName"
          choose
          options={cargoCoops.map((value) => ({
            value,
            label: value,
          }))}
        />

        {isCargoCompany(coopName) && (
          <TextInput label={cargoTrackingStrings.coopTrackNo} name="coopTrackingNo" />
        )}

        {coopName === id<CargoCoop>('チャータートラック') && (
          <>
            <TextInput label={cargoTrackingStrings.carNo} name="carNo" />
            <TextInput label={cargoTrackingStrings.carDriver} name="carDriver" />
          </>
        )}
      </Form>
    </Formik>
  );
};
